/**
 * WebSocket-клиент для real-time связи с backend (модуль WebSocketAPI).
 * Singleton, протокол JSON-RPC. Адаптирован из apostol-csms/driver/useWebSocket.ts
 * под React 17 + class components: использует EventTarget (window) вместо хуков.
 *
 * Уровни подписки:
 *   1. Server-side: один раз после авторизации шлём /observer/subscribe
 *      (auto-resubscribe при переподключении).
 *   2. Client-side: компоненты подписываются на publisher через ws.subscribe(),
 *      не задевая сервер.
 *
 * Использование:
 *   import ws from "src/api/websocket";
 *   const off = ws.subscribe("notify", (data) => { ... });
 *   // в componentWillUnmount: off();
 *
 *   ws.connect()  — после установки accessToken (Boot.jsx)
 *   ws.disconnect()  — при logout
 */

import config from "./config";
import accessToken from "./token";

const FRAME_OPEN        = 0;
// eslint-disable-next-line no-unused-vars
const FRAME_CLOSE       = 1;
const FRAME_CALL        = 2;
const FRAME_CALL_RESULT = 3;
const FRAME_CALL_ERROR  = 4;

const CALL_TIMEOUT_MS    = 10000;
const OPEN_TIMEOUT_MS    = 5000;
const RECONNECT_MAX_MS   = 30000;

// Подписки на сервере, один раз после auth. notify без фильтра заменяет
// прежний REST-polling /notification/list (online.jsx); конкретные потребители
// (report-download handler, StateLabel через onlineChanged) фильтруют события
// на клиенте по entity/class/action в своих subscribe-callback'ах.
const serverSubscriptions = {
  notify: {
    filter: {},
    params: { type: "notify" },
  },
};

let ws = null;
let isConnected = false;
let isAuthorized = false;
let reconnectTimer = null;
let reconnectAttempts = 0;

const pendingRequests = new Map();              // u → { resolve, reject, timeout }
const clientSubscriptions = new Map();          // publisher → Map<id, callback>

function uuid() {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

function log(level, message) {
  if (config.debug) {
    const colors = { info: "cyan", warn: "orange", error: "red" };
    console.log(`%c WS: ${message}`, `color: ${colors[level] || "cyan"}`);
  }
}

function notifyState() {
  window.dispatchEvent(new CustomEvent("wsState", { detail: { connected: isConnected, authorized: isAuthorized } }));
}

function getWsUrl(sessionCode) {
  const wsDomain = config.wsDomain;
  if (wsDomain) {
    return `${wsDomain}/session/${sessionCode}`;
  }
  // Fallback: derive из apiDomain
  const api = config.apiDomain || window.location.origin;
  const url = new URL(api);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return `${url.origin}/session/${sessionCode}`;
}

function send(frame) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(frame));
  }
}

function wsCall(action, payload) {
  return new Promise((resolve, reject) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      reject(new Error(`WebSocket not connected: ${action}`));
      return;
    }
    const u = uuid();
    const frame = { t: FRAME_CALL, u, a: action, p: payload };
    const timeout = setTimeout(() => {
      pendingRequests.delete(u);
      reject(new Error(`WS call timeout: ${action}`));
    }, CALL_TIMEOUT_MS);
    pendingRequests.set(u, { resolve, reject, timeout });
    send(frame);
  });
}

async function subscribeAllOnServer() {
  for (const [publisher, { filter, params }] of Object.entries(serverSubscriptions)) {
    try {
      await wsCall("/observer/subscribe", { publisher, filter, params });
      log("info", `subscribed: ${publisher}`);
    } catch (e) {
      log("warn", `subscribe ${publisher} failed: ${e.message}`);
    }
  }
}

function handleMessage(event) {
  let msg;
  try {
    msg = JSON.parse(event.data);
  } catch {
    return;
  }

  if (msg.t === FRAME_CALL_RESULT || msg.t === FRAME_CALL_ERROR) {
    const pending = pendingRequests.get(msg.u);
    if (pending) {
      clearTimeout(pending.timeout);
      pendingRequests.delete(msg.u);
      if (msg.t === FRAME_CALL_RESULT) {
        pending.resolve(msg.p);
      } else {
        pending.reject(new Error(msg.m || `WS error ${msg.c}`));
      }
    }
  } else if (msg.t === FRAME_CALL) {
    // Server push. msg.a — путь публикатора (например, "/notify"), msg.p — данные
    const publisher = (msg.a || "").replace(/^\//, "");
    const subs = clientSubscriptions.get(publisher);
    if (subs) {
      for (const cb of subs.values()) {
        try {
          cb(msg.p);
        } catch (e) {
          log("error", `subscriber callback for ${publisher}: ${e.message}`);
        }
      }
    }

    // Backward compat: ретранслируем notify-события через старый
    // window event 'onlineChanged' — withOnline HOC и StateLabel ждут
    // shape { items: { [object]: notification } }. Это позволяет отключить
    // REST-polling в online.jsx без переписывания потребителей.
    if (publisher === "notify" && msg.p && msg.p.object) {
      const items = { [msg.p.object]: msg.p };
      window.dispatchEvent(new CustomEvent("onlineChanged", { detail: { items } }));
    }
  }
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  reconnectAttempts++;
  const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), RECONNECT_MAX_MS);
  log("info", `reconnect in ${delay}ms (attempt ${reconnectAttempts})`);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

function connect() {
  if (typeof window === "undefined") return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  const token = accessToken.get();
  const session = accessToken.tk && accessToken.tk.session;

  if (!token || !session) {
    log("warn", `connect skipped: no token or session (token=${!!token}, session=${!!session})`);
    return;
  }

  const url = getWsUrl(session);
  // Код сессии в URL — те же учётные данные, что и в теле запроса: по нему
  // открывается канал уведомлений. Вырезать его в одном месте и печатать
  // в соседнем — непоследовательность, а не защита (T049).
  log("info", `connecting to ${url.replace(session, "***")}`);
  const socket = new WebSocket(url);

  socket.onopen = () => {
    isConnected = true;
    reconnectAttempts = 0;
    notifyState();
    log("info", "connected, sending OPEN with token");

    const u = uuid();
    const frame = { t: FRAME_OPEN, u, p: { token } };
    const timeout = setTimeout(() => {
      pendingRequests.delete(u);
      log("warn", "OPEN timeout, closing");
      socket.close();
    }, OPEN_TIMEOUT_MS);
    pendingRequests.set(u, {
      resolve: (p) => {
        if (p && p.authorized) {
          isAuthorized = true;
          notifyState();
          log("info", "authorized");
          subscribeAllOnServer();
        } else {
          log("warn", "OPEN responded without authorized=true");
        }
      },
      reject: (err) => {
        isAuthorized = false;
        notifyState();
        log("warn", `OPEN rejected: ${err.message}`);
      },
      timeout,
    });
    send(frame);
  };

  socket.onmessage = handleMessage;

  socket.onerror = () => {
    log("error", "socket error");
  };

  socket.onclose = (ev) => {
    isConnected = false;
    isAuthorized = false;
    ws = null;
    notifyState();
    log("info", `closed: code=${ev.code} reason=${ev.reason}`);
    scheduleReconnect();
  };

  ws = socket;
}

function disconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  reconnectAttempts = 0;
  for (const [, req] of pendingRequests) clearTimeout(req.timeout);
  pendingRequests.clear();

  if (ws) {
    ws.onclose = null;
    ws.close();
    ws = null;
  }
  isConnected = false;
  isAuthorized = false;
  notifyState();
}

/**
 * Подписаться на publisher. Возвращает функцию unsubscribe для использования
 * в componentWillUnmount.
 *
 * Сами фильтры публикатора задаются глобально в serverSubscriptions выше;
 * клиентская подписка только маршрутизирует приходящие события в callback.
 */
function subscribe(publisher, callback) {
  if (!clientSubscriptions.has(publisher)) {
    clientSubscriptions.set(publisher, new Map());
  }
  const id = uuid();
  clientSubscriptions.get(publisher).set(id, callback);
  return () => {
    const subs = clientSubscriptions.get(publisher);
    if (subs) subs.delete(id);
  };
}

const wsClient = { connect, disconnect, subscribe };

export default wsClient;
